import {
  keepPreviousData, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  ComplianceItemView, ComplianceSettingView, CredentialCheckView, CredentialContext,
  CredentialOverrideView, CredentialRuleView, DsrActionView, DsrRequestView, ErasurePlanView,
  EvidenceView, GenerateResult, GpssaFilingView, NationalisationView, OccurrenceView,
  RetentionAnswer, WpsSubmissionView,
} from '../types'

const BASE = '/v1/compliance'

export interface PlacementQuery {
  employeeId: string
  legalEntityId?: string | null
  establishmentId?: string | null
  locationId?: string | null
  gradeId?: string | null
  designationId?: string | null
  asOf?: string | null
}

export function useCredentialRules() {
  return useQuery({
    queryKey: ['compliance', 'rules'],
    queryFn: async () => (await apiClient.get<CredentialRuleView[]>(`${BASE}/credential-rules`)).data,
  })
}

export function useCredentialRuleMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['compliance'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<CredentialRuleView>(`${BASE}/credential-rules`, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<CredentialRuleView>(`${BASE}/credential-rules/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    retire: useMutation({
      mutationFn: async ({ id, lastDay }: { id: string; lastDay?: string }) =>
        (await apiClient.post<CredentialRuleView>(`${BASE}/credential-rules/${id}/retire`, null, {
          params: { lastDay },
        })).data,
      onSuccess: invalidate,
    }),
  }
}

/**
 * Where somebody stands against the rules for a placement, without acting on it.
 *
 * Read-only and side-effect free, so the transfer wizard can call it as the form changes
 * and show the position before anything is submitted.
 */
export function useCredentialCheck(query: PlacementQuery | null) {
  return useQuery({
    queryKey: ['compliance', 'check', query],
    enabled: query != null && query.employeeId !== '',
    queryFn: async () =>
      (await apiClient.get<CredentialCheckView>(`${BASE}/credential-check`, {
        params: {
          employeeId: query?.employeeId,
          legalEntityId: query?.legalEntityId || undefined,
          establishmentId: query?.establishmentId || undefined,
          locationId: query?.locationId || undefined,
          gradeId: query?.gradeId || undefined,
          designationId: query?.designationId || undefined,
          asOf: query?.asOf || undefined,
        },
      })).data,
  })
}

export function useCredentialOverrides(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['compliance', 'overrides', employeeId],
    enabled: employeeId != null,
    queryFn: async () =>
      (await apiClient.get<CredentialOverrideView[]>(`${BASE}/employees/${employeeId}/overrides`)).data,
  })
}

export function useOverrideMutations() {
  const queryClient = useQueryClient()
  // An override changes what the check says, so both caches go.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['compliance'] })

  return {
    record: useMutation({
      mutationFn: async (body: {
        employeeId: string
        credentialRuleId: string
        context: CredentialContext
        reason: string
        expiresAt?: string | null
      }) => (await apiClient.post<CredentialOverrideView>(`${BASE}/overrides`, body)).data,
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<CredentialOverrideView>(`${BASE}/overrides/${id}/revoke`)).data,
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 8 part 1: calendar, ratio, filings, retention review, data-subject requests ---

// Same base as the credential endpoints above; named separately only for brevity below.
const C = BASE

export function useComplianceItems() {
  return useQuery({
    queryKey: ['compliance', 'items'],
    queryFn: async () => (await apiClient.get<ComplianceItemView[]>(`${C}/items`)).data,
  })
}

export function useOccurrences(params: {
  entityId?: string; status?: string; category?: string; year?: number
  page?: number; size?: number
}) {
  return useQuery({
    queryKey: ['compliance', 'occurrences', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<OccurrenceView>>(`${C}/occurrences`, {
        params: {
          entityId: params.entityId || undefined,
          status: params.status || undefined,
          category: params.category || undefined,
          year: params.year,
          page: params.page,
          size: params.size,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useEvidence(occurrenceId: string | undefined) {
  return useQuery({
    queryKey: ['compliance', 'evidence', occurrenceId],
    queryFn: async () =>
      (await apiClient.get<EvidenceView[]>(`${C}/occurrences/${occurrenceId!}/evidence`)).data,
    enabled: Boolean(occurrenceId),
  })
}

export function useNationalisation(entityId: string | undefined, year: number) {
  return useQuery({
    queryKey: ['compliance', 'nationalisation', entityId, year],
    queryFn: async () =>
      (await apiClient.get<NationalisationView>(`${C}/nationalisation`, {
        params: { entityId, year },
      })).data,
    enabled: Boolean(entityId),
  })
}

export function useWpsSubmissions() {
  return useQuery({
    queryKey: ['compliance', 'wps'],
    queryFn: async () => (await apiClient.get<WpsSubmissionView[]>(`${C}/wps-submissions`)).data,
  })
}

export function useGpssaFilings() {
  return useQuery({
    queryKey: ['compliance', 'gpssa'],
    queryFn: async () => (await apiClient.get<GpssaFilingView[]>(`${C}/gpssa-filings`)).data,
  })
}

export function useRetentionReview() {
  return useQuery({
    queryKey: ['compliance', 'retention-review'],
    queryFn: async () => (await apiClient.get<RetentionAnswer[]>(`${C}/retention-review`)).data,
  })
}

export function useComplianceSettings() {
  return useQuery({
    queryKey: ['compliance', 'settings'],
    queryFn: async () => (await apiClient.get<ComplianceSettingView>(`${C}/settings`)).data,
  })
}

export function useDsrRequests() {
  return useQuery({
    queryKey: ['compliance', 'requests'],
    queryFn: async () => (await apiClient.get<DsrRequestView[]>(`${C}/requests`)).data,
  })
}

export function useDsrActions(requestId: string | undefined) {
  return useQuery({
    queryKey: ['compliance', 'actions', requestId],
    queryFn: async () =>
      (await apiClient.get<DsrActionView[]>(`${C}/requests/${requestId!}/actions`)).data,
    enabled: Boolean(requestId),
  })
}

export function useErasurePlan() {
  return useQuery({
    queryKey: ['compliance', 'erasure-plan'],
    queryFn: async () => (await apiClient.get<ErasurePlanView[]>(`${C}/erasure-plan`)).data,
    // The plan changes when the product changes, not while somebody is reading it.
    staleTime: 60 * 60 * 1000,
  })
}

export function useComplianceMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['compliance'] })

  return {
    createItem: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ComplianceItemView>(`${C}/items`, body)).data,
      onSuccess: invalidate,
    }),
    updateItem: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<ComplianceItemView>(`${C}/items/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    deactivateItem: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${C}/items/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    generate: useMutation({
      mutationFn: async (year: number) =>
        (await apiClient.post<GenerateResult>(`${C}/occurrences/generate`, null,
          { params: { year } })).data,
      onSuccess: invalidate,
    }),
    raiseOccurrence: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<OccurrenceView>(`${C}/occurrences`, body)).data,
      onSuccess: invalidate,
    }),
    completeOccurrence: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<OccurrenceView>(`${C}/occurrences/${id}/complete`, body)).data,
      onSuccess: invalidate,
    }),
    attachEvidence: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<EvidenceView>(`${C}/occurrences/${id}/evidence`, body)).data,
      onSuccess: invalidate,
    }),
    saveNationalisation: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<NationalisationView>(`${C}/nationalisation`, body)).data,
      onSuccess: invalidate,
    }),
    saveWps: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<WpsSubmissionView>(`${C}/wps-submissions/${id}`, body)).data
          : (await apiClient.post<WpsSubmissionView>(`${C}/wps-submissions`, body)).data,
      onSuccess: invalidate,
    }),
    saveGpssa: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<GpssaFilingView>(`${C}/gpssa-filings/${id}`, body)).data
          : (await apiClient.post<GpssaFilingView>(`${C}/gpssa-filings`, body)).data,
      onSuccess: invalidate,
    }),
    saveSettings: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<ComplianceSettingView>(`${C}/settings`, body)).data,
      onSuccess: invalidate,
    }),
    raiseRequest: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<DsrRequestView>(`${C}/requests`, body)).data,
      onSuccess: invalidate,
    }),
    submitRequest: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<DsrRequestView>(`${C}/requests/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    completeRequest: useMutation({
      mutationFn: async ({ id, note }: { id: string; note?: string }) =>
        (await apiClient.post<DsrRequestView>(`${C}/requests/${id}/complete`, null,
          { params: { note } })).data,
      onSuccess: invalidate,
    }),
    eraseRequest: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<DsrRequestView>(`${C}/requests/${id}/erase`)).data,
      onSuccess: invalidate,
    }),
  }
}
